/**
 * What a set and a workout add up to.
 *
 * The per-rep verdict answers "what should I change on the next rep", which is
 * the only question worth answering mid-drill. Afterwards there is a different
 * question — "how did that go" — and it needs the set as a whole: how many reps,
 * how many clean, which fault came up most, how long it took.
 *
 * Score is the share of reps with no failed criterion, and nothing cleverer.
 * A weighted score would need to say that one criterion matters more than
 * another, and nothing in docs/arm-swing-posture.md supports that ordering: the
 * thresholds are one athlete's reps read by one person. Counting clean reps is a
 * claim the numbers can carry.
 *
 * Reps that could not be judged still count as reps and are not clean. They are
 * reported separately, because "6 of 10 clean, 2 not fully seen" tells the
 * athlete to move the phone, while a bare 60% tells them to train harder.
 *
 * No DOM here, and no formatting. The receipt renders this; a check can print it
 * from Node against a recording, which is how the arm swing's cues were
 * verified.
 */
import { verdictFor } from "./coach";

export class SetScore {
  /**
   * @param exercise The exercise definition this set was performed under.
   * @param startedAt Wall-clock ms, for the receipt's set time.
   */
  constructor(exercise, startedAt = Date.now()) {
    this.exercise = exercise;
    this.startedAt = startedAt;
    this.endedAt = null;
    this.reps = 0;
    this.good = 0;
    this.partial = 0;
    /** Per criterion: `{ id, label, passed, judged }`, in the rows' own order. */
    this.criteria = [];
    /** Cue text to how many reps raised it, commonest first when read back. */
    this.faults = new Map();
    /**
     * Per informational row marked `aggregate: "sum"`: `{ id, label, unit,
     * precision, total, best, reps }`. A figure that judges nothing can still be
     * worth adding up — the air time the athlete asked to see in seconds is the
     * first of them — and a sum belongs to the set rather than to the rep, since
     * a rep already showed its own number as it happened.
     */
    this.totals = [];
  }

  /** @param scored Rows from the exercise's `scoreRep`. */
  add(scored) {
    const verdict = verdictFor(scored, this.exercise && this.exercise.cues);
    this.reps += 1;
    if (verdict.good) {
      this.good += 1;
    }
    if (verdict.partial) {
      this.partial += 1;
    }
    for (const row of scored) {
      if (row.informational && row.aggregate === "sum") {
        let total = this.totals.find((t) => t.id === row.id);
        if (total == null) {
          total = {
            id: row.id,
            label: row.label,
            unit: row.unit,
            precision: row.precision,
            total: 0,
            best: 0,
            reps: 0,
          };
          this.totals.push(total);
        }
        if (Number.isFinite(row.value)) {
          total.total += row.value;
          total.best = Math.max(total.best, row.value);
          total.reps += 1;
        }
      }
      if (row.informational) {
        // A row that judges nothing cannot pass, fail or be counted as unseen;
        // see tempo.js. It has no place in a tally of criteria.
        continue;
      }
      let entry = this.criteria.find((c) => c.id === row.id);
      if (entry == null) {
        entry = { id: row.id, label: row.label, passed: 0, judged: 0 };
        this.criteria.push(entry);
      }
      if (row.pass != null) {
        entry.judged += 1;
        if (row.pass) {
          entry.passed += 1;
        }
      }
      if (row.pass === false) {
        const cue = this.cueFor(row);
        this.faults.set(cue, (this.faults.get(cue) || 0) + 1);
      }
    }
  }

  cueFor(row) {
    const cues = (this.exercise && this.exercise.cues) || {};
    return cues[row.id] || row.label;
  }

  end(at = Date.now()) {
    this.endedAt = at;
    return this;
  }

  get seconds() {
    const end = this.endedAt == null ? Date.now() : this.endedAt;
    return Math.max(0, Math.round((end - this.startedAt) / 1000));
  }

  /** Share of reps with no failed criterion, 0-100, or null for an empty set. */
  get percent() {
    return this.reps === 0 ? null : Math.round((100 * this.good) / this.reps);
  }

  /**
   * The set as plain data, for storage and for the backend this is one day
   * going to be posted to.
   *
   * Deliberately not the whole object: `exercise` is a module, not data, so only
   * its id is kept and the definition is looked up again on the way back. What
   * is stored is the SUMMARY — counts and criterion tallies — never the samples
   * or the landmarks, which is the same line the rest of the app holds: nothing
   * derived from the video is kept beyond the figures the athlete was shown.
   */
  toJSON() {
    return {
      exercise: this.exercise && this.exercise.id,
      startedAt: this.startedAt,
      endedAt: this.endedAt,
      reps: this.reps,
      good: this.good,
      partial: this.partial,
      criteria: this.criteria,
      totals: this.totals,
      faults: [...this.faults.entries()],
    };
  }

  /**
   * Rebuilds a set from `toJSON()`.
   *
   * Every field is checked rather than trusted: this comes back from storage,
   * where an older build, a half-written record or a hand edit all look the
   * same. A set that cannot be rebuilt is dropped by the caller rather than
   * printed as a line of NaN.
   *
   * @param exercise The definition for `data.exercise`, or null if that drill no
   *   longer exists — the line then still prints, under its stored id.
   */
  static fromJSON(data, exercise) {
    if (data == null || typeof data !== "object") {
      return null;
    }
    const reps = count(data.reps);
    if (reps === 0) {
      return null;
    }
    const set = new SetScore(
      exercise || { id: data.exercise, name: data.exercise || "Drill" },
      count(data.startedAt)
    );
    set.endedAt = count(data.endedAt) || set.startedAt;
    set.reps = reps;
    set.good = Math.min(reps, count(data.good));
    set.partial = Math.min(reps, count(data.partial));
    set.criteria = Array.isArray(data.criteria)
      ? data.criteria
          .filter((c) => c && typeof c.id === "string")
          .map((c) => ({
            id: c.id,
            label: typeof c.label === "string" ? c.label : c.id,
            passed: count(c.passed),
            judged: count(c.judged),
          }))
      : [];
    set.totals = Array.isArray(data.totals)
      ? data.totals
          .filter(
            (t) =>
              t &&
              typeof t.id === "string" &&
              Number.isFinite(t.total) &&
              t.total >= 0
          )
          .map((t) => ({
            id: t.id,
            label: typeof t.label === "string" ? t.label : t.id,
            unit: typeof t.unit === "string" ? t.unit : "",
            precision: count(t.precision),
            total: t.total,
            best: Number.isFinite(t.best) && t.best > 0 ? t.best : 0,
            reps: count(t.reps),
          }))
      : [];
    set.faults = new Map(
      Array.isArray(data.faults)
        ? data.faults.filter(
            ([cue, n]) => typeof cue === "string" && count(n) > 0
          )
        : []
    );
    return set;
  }

  /** Faults by how many reps raised them, commonest first. */
  get topFaults() {
    return [...this.faults.entries()]
      .map(([cue, count]) => ({ cue, count }))
      .sort((a, b) => b.count - a.count);
  }
}

/**
 * The sets performed since the page was opened, in order.
 *
 * A workout rather than a set is what the receipt is of, because a set is not
 * what anyone finishes: the athlete does a set, catches their breath, does
 * another, maybe picks a different drill. So the receipt is itemised like a till
 * roll, one line per set, and the total is over everything done.
 */
export class Workout {
  constructor() {
    this.sets = [];
  }

  /** @param set A finished `SetScore`. Empty sets are dropped. */
  add(set) {
    if (set.reps > 0) {
      this.sets.push(set);
    }
    return this;
  }

  get reps() {
    return this.sets.reduce((total, set) => total + set.reps, 0);
  }

  get good() {
    return this.sets.reduce((total, set) => total + set.good, 0);
  }

  get partial() {
    return this.sets.reduce((total, set) => total + set.partial, 0);
  }

  get seconds() {
    return this.sets.reduce((total, set) => total + set.seconds, 0);
  }

  get percent() {
    return this.reps === 0 ? null : Math.round((100 * this.good) / this.reps);
  }

  /** True when any set was scored against thresholds nobody has calibrated. */
  get uncalibrated() {
    return this.sets.some((set) => set.exercise && !set.exercise.calibrated);
  }

  /**
   * The aggregated informational figures across every set, by id, in the order
   * they first appeared. One line on the ticket rather than one per set.
   */
  get totals() {
    const byId = new Map();
    for (const set of this.sets) {
      for (const t of set.totals || []) {
        const entry = byId.get(t.id);
        if (entry == null) {
          byId.set(t.id, { ...t });
        } else {
          entry.total += t.total;
          entry.best = Math.max(entry.best, t.best);
          entry.reps += t.reps;
        }
      }
    }
    return [...byId.values()];
  }

  get topFaults() {
    const totals = new Map();
    for (const set of this.sets) {
      for (const { cue, count } of set.topFaults) {
        totals.set(cue, (totals.get(cue) || 0) + count);
      }
    }
    return [...totals.entries()]
      .map(([cue, count]) => ({ cue, count }))
      .sort((a, b) => b.count - a.count);
  }

  reset() {
    this.sets = [];
  }
}

/**
 * A letter for the score, console-style.
 *
 * Bands rather than a curve, and deliberately coarse: the measurement's own
 * resolution is one rep, so on a ten-rep set every band edge is a whole rep
 * anyway. S is kept for a set with nothing wrong in it, because a rank that can
 * be reached with a fault in it is not worth reaching.
 */
function count(value) {
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

export function rankFor(percent) {
  if (percent == null) {
    return "-";
  }
  if (percent >= 100) {
    return "S";
  }
  if (percent >= 90) {
    return "A";
  }
  if (percent >= 75) {
    return "B";
  }
  if (percent >= 50) {
    return "C";
  }
  return "D";
}
